import { defineStore } from 'pinia'
import { computed, nextTick, ref } from 'vue'
import { resetAllStores, useUserStore } from '@/store'
import { useRouter } from 'vue-router'
import { http } from '@/utils'
import { LOGIN_ROUTE } from '@/router/constants'

/**
 * 就绪信号：用于路由守卫等待认证状态恢复完成
 * 采用 Promise 模式避免竞态条件
 */
let _resolveReadyList: Array<() => void> = []

export const useAuthStore = defineStore('auth', () => {
  const user = useUserStore()

  const isAuthenticated = computed(() => !!user.isLoggedIn)

  const isLoggingOut = ref(false)

  /**
   * 状态恢复就绪标志
   * 在 app.mount() 前由 hydrateFromStorage() 设置为 true
   */
  const ready = ref(false)

  async function setToken(_t: string, _r?: string) {
    console.warn(
      '[auth] setToken 已不推荐使用，请走 userStore.onAuthSuccess 或 login/register 流程',
    )
  }

  async function refreshToken() {
    // 预留扩展点：如果有 refresh token 流程，可在此实现，然后调用 user.onAuthSuccess()
  }

  async function logout() {
    try {
      isLoggingOut.value = true
      await http.post('/api/pub/loginOut')

      http.cancelAll?.()
      http.setAuthToken?.(null)
    } catch (error) {
      console.error('[auth] logout error:', error)
    } finally {
      if (window.location.pathname !== LOGIN_ROUTE.path) {
        window.location.replace(LOGIN_ROUTE.path)
      }
      resetAllStores()
      isLoggingOut.value = false
    }
  }

  /**
   * 角色判断：支持字符串或字符串数组
   */
  function hasRole(role: string): boolean {
    const r = (user.userInfo as any)?.role
    if (!r) return false
    return Array.isArray(r) ? r.includes(role) : r === role
  }

  /**
   * 权限点判断：你们若在 userInfo 里有 permissions: string[]，这里就能用上
   * 若暂时没有，默认返回 true（避免全站被 403）
   */
  function hasAuthority(perm: string): boolean {
    const list = (user.userInfo as any)?.permissions
    if (Array.isArray(list)) return list.includes(perm)
    return true
  }

  /**
   * 功能开关（可选）：如果有全局开关服务，可在此对接
   */
  const flags = {
    isEnabled(key: string) {
      const enabledList: string[] | undefined = (user.userInfo as any)?.featureFlags
      if (Array.isArray(enabledList)) return enabledList.includes(key)
      return true
    },
  }

  /**
   * 策略校验（可选，ABAC/ReBAC）：统一接口供 accessGuard 调用
   * 这里默认放行，你可以接入后端策略服务
   */
  async function can(_policy: { action: string; resource?: any }): Promise<boolean> {
    // TODO: 接入策略引擎
    return true
  }

  /**
   * 从 localStorage 恢复认证状态（应在 app.mount() 前调用）
   *
   * 目的：
   * 1. 在路由守卫执行前完成状态恢复，避免竞态条件
   * 2. 保持单一数据源（只依赖 user store，不在守卫里读 localStorage）
   * 3. 为后续 token 校验、刷新等扩展预留接口
   *
   * 调用时机：main.ts 中 app.use(pinia) 之后，app.mount() 之前
   */
  function hydrateFromStorage(): void {
    if (ready.value) {
      if (import.meta.env.DEV) {
        console.warn('[auth] hydrateFromStorage 已执行过，跳过重复调用')
      }
      return
    }

    // userStore 使用 useStorage，会自动从 localStorage 恢复
    // 这里只需确保 userStore 已经完成初始化
    // 由于 userStore 在 auth store 之前初始化，状态已经恢复

    ready.value = true

    // 触发所有等待的 Promise
    _resolveReadyList.splice(0).forEach((resolve) => resolve())
  }

  /**
   * 等待认证状态就绪（用于路由守卫）
   *
   * 使用场景：
   * ```ts
   * export const loginGuard: NavigationGuard = async (to, from, next) => {
   *   const auth = useAuthStore()
   *   await auth.whenReady()  // 确保状态已恢复
   *
   *   if (!auth.isAuthenticated && !isPublicRoute(to)) {
   *     return next({ name: 'login' })
   *   }
   *   return next()
   * }
   * ```
   *
   * @returns Promise，在 hydrateFromStorage() 完成后 resolve
   */
  function whenReady(): Promise<void> {
    if (ready.value) {
      return Promise.resolve()
    }
    return new Promise<void>((resolve) => {
      _resolveReadyList.push(resolve)
    })
  }

  return {
    // state
    isAuthenticated,
    isLoggingOut,
    ready,

    // actions
    setToken,
    refreshToken,
    logout,
    hydrateFromStorage,
    whenReady,

    // authz
    hasAuthority,
    hasRole,
    flags,
    can,
  }
})
